import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalendarClock, MousePointer2, Image as ImageIcon, ArrowRight, ShieldCheck, Megaphone, Bot, Inbox, Users, Repeat, BadgeCheck, LineChart, TrendingUp, Zap, Workflow, Heart, Building2, Lock, Sparkles, ShoppingBag, GraduationCap, Stethoscope, Home, Store, Plane, CheckCircle2 } from "lucide-react";
import DemoFormDialog from "@/components/DemoFormDialog";
import ChatMockup from "@/components/ChatMockup";
import TestimonialGrid from "@/components/TestimonialGrid";
import FaqAccordion from "@/components/FaqAccordion";
import FinalCta from "@/components/FinalCta";
import { FEATURES, WHY_CHOOSE, FAQS, PRICING, BRAND } from "@/data/site";
import { getOffering } from "@/data/catalog";
import { SectionHeading, WhyWhatsApp, IncludedStrip } from "@/components/Sections";
import { HowGrabNChatWorks, CustomerJourney, ProductDemoTabs } from "@/components/HomeSections";
import { useSeo, FaqSchema } from "@/lib/seo";

const featureIcons = { ShieldCheck, Megaphone, Bot, Inbox, Users, Repeat, BadgeCheck, LineChart };
const whyIcons = { TrendingUp, Zap, Workflow, Heart, Building2, Lock };

const INDUSTRIES = [
  { slug: "ecommerce", label: "Ecommerce", icon: ShoppingBag, desc: "Cart recovery, order updates and catalog sales" },
  { slug: "education", label: "Education", icon: GraduationCap, desc: "Admission enquiries, counselling and fee reminders" },
  { slug: "healthcare", label: "Healthcare", icon: Stethoscope, desc: "Appointments, reminders and patient follow-ups" },
  { slug: "real-estate", label: "Real Estate", icon: Home, desc: "Property leads, brochures and site visits" },
  { slug: "retail", label: "Retail", icon: Store, desc: "Offers, loyalty and in-store QR chats" },
  { slug: "travel", label: "Travel", icon: Plane, desc: "Enquiries, itineraries and trip updates" },
];

const BROADCAST_POINTS = [
  ["Rich, personalised messages", "Send images, videos, documents and offers with the customer's name and details filled in.", ImageIcon],
  ["Buttons that drive action", "Add quick-reply and call-to-action buttons so customers can buy, book or reply in one tap.", MousePointer2],
  ["Schedule & measure", "Schedule broadcasts in advance and track sent, delivered, read and clicked in real time.", CalendarClock],
];

const HIGHLIGHTS = ["bulk-whatsapp-marketing", "whatsapp-chatbot", "shared-team-inbox", "whatsapp-crm", "ads-manager", "whatsapp-calling", "qr-kits", "ai-content-studio"].map(getOffering).filter(Boolean);

export default function HomePage() {
  useSeo({
    title: "WhatsApp Business API & Marketing Automation | GrabNChat",
    description: "GrabNChat is a WhatsApp Business API platform for marketing, sales and support: bulk WhatsApp campaigns, broadcasts, no-code chatbots, shared inbox, CRM and analytics.",
    path: "/",
  });

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0 bg-dots opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-200 bg-white text-xs font-medium text-slate-700 mb-6">
                <Sparkles className="w-3.5 h-3.5 text-[#25D366]" /> Official WhatsApp Business API · Meta Tech Provider
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
                WhatsApp Business API &amp; <span className="text-gradient-green">Marketing Automation Platform</span>
              </h1>
              <p className="mt-5 font-display text-xl sm:text-2xl font-semibold text-slate-800 max-w-xl">
                Marketing, Sales &amp; Customer Support — All in One WhatsApp Platform
              </p>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-xl">
                Run <Link to="/products/bulk-whatsapp-marketing" className="font-medium text-slate-800 underline decoration-[#25D366]/40 underline-offset-4 hover:text-[#25D366]">bulk WhatsApp campaigns</Link>, build <Link to="/products/whatsapp-chatbot" className="font-medium text-slate-800 underline decoration-[#25D366]/40 underline-offset-4 hover:text-[#25D366]">no-code chatbots</Link>, reply from a <Link to="/products/shared-team-inbox" className="font-medium text-slate-800 underline decoration-[#25D366]/40 underline-offset-4 hover:text-[#25D366]">shared team inbox</Link> and track every customer in a <Link to="/products/whatsapp-crm" className="font-medium text-slate-800 underline decoration-[#25D366]/40 underline-offset-4 hover:text-[#25D366]">WhatsApp CRM</Link> — powered by the official WhatsApp Business API.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <DemoFormDialog trigger={
                  <Button className="bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full px-7 h-12 font-semibold" data-testid="hero-book-demo-btn">
                    Book Free Demo <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                } />
                <a href={`${BRAND.appUrl}/login`}>
                  <Button variant="outline" className="w-full bg-white border-slate-200 hover:border-slate-300 text-slate-900 rounded-full px-7 h-12 font-semibold" data-testid="hero-start-trial-btn">
                    Start Free Trial
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-600">
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#25D366]" /> 7-day free trial</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#25D366]" /> No credit card required</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#25D366]" /> Live in 24 hours</span>
                <a href="#how-it-works" className="font-semibold text-slate-800 hover:text-[#25D366]" data-testid="hero-how-it-works">See how it works ↓</a>
              </div>
            </div>
            <div className="relative"><ChatMockup /></div>
          </div>
        </div>
      </section>


      {/* Broadcast marketing */}
      <section className="py-20 md:py-28 overflow-hidden" data-testid="broadcast-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="min-w-0">
            <SectionHeading eyebrow="Bulk WhatsApp marketing" title="WhatsApp Marketing Made Simple"
              sub="Broadcast personalised, rich messages to every opted-in customer — officially, on the WhatsApp Business API — and see who reads, clicks and replies." />
            <ul className="mt-8 space-y-5">
              {BROADCAST_POINTS.map(([t, d, I]) => (
                <li key={t} className="flex gap-4">
                  <span className="w-11 h-11 shrink-0 rounded-xl bg-[#25D366]/10 text-[#25D366] grid place-items-center"><I className="w-5 h-5" /></span>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-slate-900">{t}</h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={`${BRAND.appUrl}/login`}>
                <Button className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full px-7 h-12 font-semibold">Start for Free <ArrowRight className="ml-2 w-4 h-4" /></Button>
              </a>
              <Link to="/products/bulk-whatsapp-marketing">
                <Button variant="outline" className="w-full rounded-full px-7 h-12 font-semibold">Explore WhatsApp Marketing</Button>
              </Link>
            </div>
          </div>
          <div className="min-w-0 relative">
            <div className="absolute -inset-6 bg-[#25D366]/10 blur-3xl rounded-3xl" aria-hidden />
            <div className="relative bg-white border border-slate-200 rounded-3xl shadow-xl p-6">
              <div className="flex items-center justify-between">
                <div className="font-display font-semibold text-slate-900">Diwali Sale – broadcast</div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#25D366]/10 text-[#1DA851]">Sent</span>
              </div>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["Sent", "12,480"], ["Delivered", "12,302"], ["Read", "11,050"], ["Clicked", "4,212"]].map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">{k}</div>
                    <div className="mt-1 font-display text-xl font-bold text-slate-900">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-2xl bg-[#E7FFDB] p-4 text-sm text-slate-800 max-w-sm">
                <div className="font-semibold">Hi Priya 👋</div>
                <p className="mt-1">Our Diwali sale is live — flat 30% off on everything till Sunday.</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <span className="text-center rounded-lg bg-white py-1.5 text-[#128C7E] font-semibold text-xs">Shop now</span>
                  <span className="text-center rounded-lg bg-white py-1.5 text-[#128C7E] font-semibold text-xs">Talk to us</span>
                </div>
              </div>
              <p className="mt-4 text-[11px] text-slate-400">Illustrative example</p>
            </div>
          </div>
        </div>
      </section>

      {/* Highlight features */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="highlights-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="One platform" title="Everything You Need to Grow on WhatsApp"
            sub="WhatsApp campaigns, chatbots, a team inbox and CRM — plus ads, voice calls, QR codes and AI content that bring customers into the chat." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HIGHLIGHTS.map((o) => (
              <Link key={o.slug} to={`/products/${o.slug}`} className="group min-w-0 bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`highlight-${o.slug}`}>
                <span className="w-12 h-12 rounded-2xl grid place-items-center bg-[#0F172A] text-white"><o.icon className="w-6 h-6" /></span>
                <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{o.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{o.short}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#25D366]">Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ProductDemoTabs />

      {/* Advanced features */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Built for conversions" title="Powerful WhatsApp Features for Your Business"
            sub="WhatsApp automation, broadcasts, chatbots, CRM and analytics for marketing, sales and customer support — on the official WhatsApp Business API." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = featureIcons[f.icon] || ShieldCheck;
              return (
                <Link key={f.title} to={f.to || "/features"} className="group bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md hover:-translate-y-1 hover:border-[#25D366] transition-all" data-testid={`feature-card-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-[#25D366]/10 grid place-items-center text-[#25D366] mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">{f.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#25D366]">Learn more <ArrowRight className="w-4 h-4" /></span>
                </Link>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link to="/features" className="inline-flex items-center gap-2 font-semibold text-slate-900 hover:text-[#25D366]">See all features <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <HowGrabNChatWorks />
      <CustomerJourney />
      <IncludedStrip />

      {/* Industries */}
      <section id="industries" className="py-20 md:py-28 scroll-mt-20" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">WhatsApp Solutions for Every Industry</h2>
            <p className="mt-4 text-lg text-slate-600">Ready-made WhatsApp automation workflows for how your business actually runs.</p>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {INDUSTRIES.map((it) => (
              <Link to={`/industries/${it.slug}`} key={it.slug}
                className="group bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#25D366] hover:-translate-y-1 transition-all"
                data-testid={`industry-card-${it.slug}`}>
                <div className="w-11 h-11 rounded-xl bg-slate-100 grid place-items-center text-slate-700 group-hover:bg-[#25D366] group-hover:text-white transition-colors">
                  <it.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-slate-900">WhatsApp for {it.label}</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{it.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <WhyWhatsApp />

      {/* Why Choose */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Why Businesses Choose GrabNChat</h2>
              <p className="mt-4 text-lg text-slate-600">From startups to enterprises, businesses use GrabNChat to convert more leads, support faster and run powerful WhatsApp campaigns at scale.</p>
              <div className="mt-8">
                <DemoFormDialog trigger={
                  <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full px-6 h-11 font-semibold" data-testid="why-cta-btn">
                    Book a free demo <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                } />
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {WHY_CHOOSE.map((w, i) => {
                const Icon = whyIcons[w.icon] || TrendingUp;
                return (
                  <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`why-card-${i}`}>
                    <div className="w-10 h-10 rounded-lg bg-[#0F172A] text-white grid place-items-center mb-3">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-display text-base font-semibold text-slate-900">{w.title}</h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Loved by growing teams</h2>
            <p className="mt-4 text-lg text-slate-600">Real stories from businesses that scaled with GrabNChat.</p>
          </div>
          <div className="mt-12"><TestimonialGrid /></div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="py-20 md:py-28" data-testid="pricing-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Simple, Transparent Pricing</h2>
            <p className="mt-4 text-lg text-slate-600">Start free. Scale as you grow. No surprises.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {PRICING.map((p, i) => (
              <div key={i} className={`relative rounded-2xl border p-7 ${p.popular ? "border-[#25D366] bg-white shadow-xl" : "border-slate-200 bg-white"}`} data-testid={`pricing-card-${p.name.toLowerCase()}`}>
                {p.popular && <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[#25D366] text-white text-xs font-semibold">Most popular</div>}
                <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{p.desc}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold text-slate-900">{p.price}</span>
                  <span className="text-slate-500">{p.period}</span>
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f, k) => (
                    <li key={k} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-[#25D366] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-7">
                  <Link to="/pricing">
                    <Button className={`w-full rounded-full ${p.popular ? "bg-[#25D366] hover:bg-[#1DA851]" : "bg-[#0F172A] hover:bg-[#1E293B]"} text-white font-semibold`} data-testid={`pricing-cta-${p.name.toLowerCase()}`}>
                      {p.cta}
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-slate-50 scroll-mt-20" id="faq" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 text-center">Frequently Asked Questions</h2>
          <p className="mt-4 text-lg text-slate-600 text-center">Everything you need to know about GrabNChat.</p>
          <div className="mt-12"><FaqAccordion faqs={FAQS} /></div>
          <FaqSchema faqs={FAQS} />
        </div>
      </section>

      <FinalCta title="Ready to Grow Your Business on WhatsApp?" sub="Start a 7-day free trial, or book a free demo and we'll help you set up your first campaign and chatbot." ctaSource="home" />
    </div>
  );
}
